// src/data/stockTopUpPrefill.ts
// -----------------------------
// Repository for `stock_top_up_prefill` (db-163) — the Stock Top-ups Phase 5 holding
// table. Zephyr staff PREPARE a top-up for a DFV Service (insertPrefill) from the DFV
// Service editor; the service later loads its own PENDING prefill after login
// (listPendingPrefillsForShelter) and submits it read-only via the normal member path,
// which marks the row consumed (submit_stock_top_up_atomic, db-164). ISOLATED module —
// wraps ONLY the prefill table; nothing here touches the fit-out request path. Typed via
// getTypedClient(). RLS: staff (admin/superuser) manage all rows; a member may read +
// consume only its own service's rows (user_can_use_shelter_id).

import type { Database, Json } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type StockTopUpPrefillRow = Database["public"]["Tables"]["stock_top_up_prefill"]["Row"];
type PrefillInsert = Database["public"]["Tables"]["stock_top_up_prefill"]["Insert"];

type WriteResult = { data: { id: string } | null; error: { message: string } | null };

// Staff PREPARE a top-up for a service. `payload` is the capture form state (TopUpState);
// stored as JSON so the wizard rehydrates it losslessly for the read-only walkthrough.
// `preparedBy` is the staffer's profile id (audit). RLS allows the INSERT only for
// admins/superusers. Returns the new row's id + error. Null client → error-free no-op.
export async function insertPrefill(
  shelterId: string,
  payload: object,
  note?: string | null,
  preparedBy?: string | null
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const row = {
    shelter_id: shelterId,
    payload: payload as Json,
    note: note ?? null,
    prepared_by: preparedBy ?? null,
    status: "pending",
  } as PrefillInsert;
  const { data, error } = await c.from("stock_top_up_prefill").insert(row).select("id").single();
  if (error) logger.warn("insertPrefill failed:", error.message);
  return { data: (data as unknown as { id: string }) ?? null, error };
}

// A service's PENDING prefills, newest first — the member-side load behind the deep-link /
// dashboard banner. RLS scopes this to the caller's own service (user_can_use_shelter_id),
// so a bearer token in the URL is never the authority: the session is. [] on error / no client.
export async function listPendingPrefillsForShelter(
  shelterId: string
): Promise<StockTopUpPrefillRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  const { data, error } = await c
    .from("stock_top_up_prefill")
    .select("*")
    .eq("shelter_id", shelterId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) {
    logger.warn("listPendingPrefillsForShelter failed:", error.message);
    return [];
  }
  return (data ?? []) as unknown as StockTopUpPrefillRow[];
}

// A single prefill row by id, or null. Used when a deep-link names a specific prefill; RLS
// still scopes visibility (a member sees only its own service's row, staff see all).
export async function getPrefillById(
  id: string | null | undefined
): Promise<StockTopUpPrefillRow | null> {
  const c = getTypedClient();
  if (!c || !id) return null;
  const { data, error } = await c
    .from("stock_top_up_prefill")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) {
    logger.warn("getPrefillById failed:", error.message);
    return null;
  }
  return (data as unknown as StockTopUpPrefillRow) ?? null;
}

// Staff cancel a prepared top-up before it's consumed (soft — sets status; the row is kept).
// RLS restricts this to admins/superusers. Only a still-PENDING row is touched, so a top-up the
// service has already submitted (consumed) can never be flipped to cancelled. `.select("id")`
// makes a write that matched nothing (already submitted / already cancelled / RLS-filtered)
// come back as an error instead of a silent success. Null client → error-free no-op.
export async function cancelPrefill(id: string): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { data, error } = await c
    .from("stock_top_up_prefill")
    .update({ status: "cancelled" })
    .eq("id", id)
    .eq("status", "pending")
    .select("id");
  if (error) {
    logger.warn("cancelPrefill failed:", error.message);
    return { error };
  }
  if (!data || (data as unknown[]).length === 0) {
    return {
      error: { message: "Not cancelled — it may already have been submitted or cancelled." },
    };
  }
  return { error: null };
}

// The current status of one prefill: "pending" / "consumed" / "cancelled", null when the
// caller can't see it (gone), or undefined when the lookup itself failed (unknown). Used by
// the service's review page to refuse submitting a top-up staff have since cancelled.
export async function getPrefillStatus(id: string): Promise<string | null | undefined> {
  const c = getTypedClient();
  if (!c) return undefined;
  const { data, error } = await c
    .from("stock_top_up_prefill")
    .select("status")
    .eq("id", id)
    .maybeSingle();
  if (error) {
    logger.warn("getPrefillStatus failed:", error.message);
    return undefined;
  }
  return (data as { status?: string } | null)?.status ?? null;
}
